import React, { useEffect, useRef, useState } from 'react';
import { Image } from 'antd';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Checklist from '@editorjs/checklist';
import Quote from '@editorjs/quote';
import Code from '@editorjs/code';
import ImageTool from '@editorjs/image';

const EditorReadOnly = ({ data }) => {
  const editorRef = useRef(null);
  const holderId = useRef(`editorjs-readonly-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const dataRef = useRef(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);

  const parseData = (rawData) => {
    if (!rawData) return null;
    try {
      const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      if (parsed && parsed.blocks && parsed.blocks.length > 0) {
        return parsed;
      }
    } catch (e) {}
    return null;
  };

  useEffect(() => {
    const parsedData = parseData(data);
    const dataKey = parsedData ? JSON.stringify(parsedData) : null;

    if (!parsedData) {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
        dataRef.current = null;
      }
      return;
    }

    if (dataRef.current === dataKey) {
      return;
    }

    if (editorRef.current) {
      editorRef.current.render(parsedData);
      dataRef.current = dataKey;
      return;
    }

    const holderEl = document.getElementById(holderId.current);
    if (!holderEl) return;

    const editor = new EditorJS({
      holder: holderId.current,
      minHeight: 0,
      data: parsedData,
      readOnly: true,
      tools: {
        header: { class: Header },
        list: { class: List },
        checklist: { class: Checklist },
        quote: { class: Quote },
        code: { class: Code },
        image: {
          class: ImageTool,
          config: {
            uploader: {
              uploadByFile: () => Promise.resolve({ success: 0 })
            }
          }
        }
      }
    });

    editorRef.current = editor;
    dataRef.current = dataKey;

    // 等待编辑器渲染完成后，为图片添加点击事件
    setTimeout(() => {
      const holderEl = document.getElementById(holderId.current);
      if (holderEl) {
        const images = holderEl.querySelectorAll('img');
        const imageUrls = [];
        
        images.forEach((img, index) => {
          imageUrls.push(img.src);
          img.style.pointerEvents = 'auto';
          img.style.cursor = 'zoom-in';
          img.style.transition = 'transform 0.2s';
          
          // 鼠标悬停效果
          img.addEventListener('mouseenter', () => {
            img.style.transform = 'scale(1.02)';
          });
          
          img.addEventListener('mouseleave', () => {
            img.style.transform = 'scale(1)';
          });
          
          // 双击打开预览
          img.addEventListener('dblclick', (e) => {
            e.preventDefault();
            e.stopPropagation();
            setPreviewImages(imageUrls);
            setCurrentImageIndex(index);
            setPreviewVisible(true);
          });
        });
      }
    }, 500);

    return () => {
      if (editorRef.current && typeof editorRef.current.destroy === 'function') {
        editorRef.current.destroy();
        editorRef.current = null;
        dataRef.current = null;
      }
    };
  }, [data]);

  const parsedData = parseData(data);

  if (!parsedData) {
    return <span style={{ color: '#999' }}>未填写</span>;
  }

  return (
    <>
      <div 
        id={holderId.current}
        className="editorjs-readonly"
        style={{ 
          pointerEvents: 'none',
          userSelect: 'text'
        }}
      />
      
      {/* 图片预览 */}
      {previewImages.length > 0 && (
        <Image
          style={{ display: 'none' }}
          src={previewImages[currentImageIndex]}
          preview={{
            visible: previewVisible,
            onVisibleChange: (visible) => setPreviewVisible(visible),
            current: currentImageIndex,
            src: previewImages,
            onChange: (current) => setCurrentImageIndex(current),
          }}
        />
      )}
    </>
  );
};

export default EditorReadOnly;
